import {
  Link,
  Text,
} from '@chakra-ui/react';
import {
  useWalkingCycling,
} from '@src/modules/performance/context/reports-api-hooks';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import LineRangeAreaChart from '@src/modules/performance/components/charts/LineRangeAreaChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Walking and Cycling',
  content: [
    {
      title: 'New and improved walking and cycling facilities (excl. low cost, low risk)',
      description:
  <Text fontSize="inherit" mb={2}>
    The expenditure on the construction/implementation of the new or improved cycle and walking facilities and shared pedestrian and cycle paths.
    This excludes low cost cycle and walking facilities completed under the Minor Improvement category.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA Data and Tools</Link>
  </Text>,
    },
  ],
};

function TransportProgrammeDeliveryWalkingCycling() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: walkingCycling, isLoading: isLoadingWalkingCycling } = useWalkingCycling();
  const isLoading = isLoadingWalkingCycling;
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isNational) {
    chartFilters = ['Peer', 'Percentile'];
  } else if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  const walkingCyclingYears = [...new Set(walkingCycling?.map((item) => item.Year))];
  const walkingCyclingData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: walkingCycling?.map((item) => ({ x: item.Year, y: item.FundValue / 1000000 })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: walkingCycling?.map((item) => ({ x: item.Year, y: item.PeerGroupValue / 1000000 })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th Percentile',
    type: 'rangeArea',
    data: walkingCycling?.map((item) => ({ x: item.Year, y: [item.LowPercentile / 1000000, item.HighPercentile / 1000000] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const walkingCyclingChartSeries = filterChartDataByName(
    walkingCyclingData,
    chartFilters,
  );

  const markers = isRegionNationalOrStateHighways ? {
    size: [3, 0],
    hover: {
      size: 6,
    },
  } : {
    size: [3, 3, 0],
    hover: {
      size: 6,
    },
  };

  const stroke = isRegionNationalOrStateHighways ? {
    curve: 'straight',
    width: [2, 0],
  } : {
    curve: 'straight',
    width: [2, 2, 0],
  };

  return (
    <ReportSection
      title="Walking and Cycling"
      informationSource={informationSource}
      isLoading={isLoading || !walkingCycling}
      lockYear={walkingCycling?.[0]?.LockYear}
    >
      <LineRangeAreaChart
        title={['New/improved walking & cycling facilities', '(Excl. low cost, low risk)']}
        categories={walkingCyclingYears}
        series={walkingCyclingChartSeries}
        type="rangeArea"
        width="280"
        height="280"
        stroke={stroke}
        markers={markers}
        prefix="$"
        suffix="M"
        decimalPoints={1}
        yAxisDecimalPoints={1}
      />
    </ReportSection>
  );
}

export default TransportProgrammeDeliveryWalkingCycling;
